import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { sortItemsByPredefinedOrder } from "@components/inputs/select/SelectAPI";
import {
    ifAny,
    IFieldDef,
    IFieldInfoProperties,
    IGetValueArgs,
    isFieldReadOnly,
    not,
    TGetValueFn
} from "@components/smart/FieldInfo";
import { withDisplayName } from "@components/smart/GeneralFieldDefinition";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { TFilterDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { fetchAndSetItemsByInfo } from "@components/smart/smartSelect/SmartSelectAPI";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { ICellValueObject, TCellValue } from "@components/table";
import { getBoundValue, setBoundValue } from "@odata/Data.utils";
import {
    EntitySetName,
    ICurrencyEntity,
    IPrEmployeeEntity,
    IPrEmploymentEntity,
    MpsvTaxIdentifierTypeEntity,
    PrEmployeeEntity,
    PrEmploymentEntity,
    PrEmploymentTypeEntity
} from "@odata/GeneratedEntityTypes";
import {
    MpsvTaxIdentifierTypeCode,
    PrEmployeeStateCode,
    PrSubmissionTypeCode,
    PrWorkPermitTypeCode
} from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { formatValue, IFormatOptions, transformToODataString } from "@odata/OData.utils";
import { ISmartTemporalPropertyDialogCustomData } from "@odata/TemporalUtils";
import { getCompanyCurrencyEntity } from "@utils/CompanyUtils";
import { isNewEntity } from "@utils/definition.utils";
import { isObjectEmpty } from "@utils/general";
import { logger } from "@utils/log";
import { universalPersonNameFormatter } from "@utils/string";
import i18next from "i18next";

import { DASH_CHARACTER } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, NavigationSource, TextAlign, ValueType } from "../../../enums";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { createPath, IEntity, TEntityKey } from "../../../odata/BindingContext";
import { ROUTE_EMPLOYEES } from "../../../routes";
import { getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import { transformUserSelectItems, userNameWithAvatarFormatter } from "../../admin/users/Users.utils";
import { TFieldDefinition } from "../../PageUtils";

export const ShowContactAddressPath = "##ShowContactAddress##";
export const ShowCzechAddressPath = "##ShowCzechAddress##";
export const ShowDisabilityPath = "##ShowDisability##";
export const ShowPensionPath = "##ShowPension##";
export const ShowStudyPath = "##ShowStudy##";
export const ShowOtherIdentifiers = "##ShowOtherIdentifiers##";
export const ShowForeignInsuranceCarrierPath = "##ShowForeignInsuranceCarrierPath##";
export const ShowSocialInsuranceDiscountLocalPath = "##SocialInsuranceDiscount##";

export const PreviousEmploymentDurationDaysPath = "##PreviousEmploymentDurationDays##";
export const PreviousEmploymentDurationYearsPath = "##PreviousEmploymentDurationYears##";
export const IdentificationTypeLocalPath = BindingContext.localContext("IdentificationType");

export enum IdentificationType {
    DateOfBirth = "DateOfBirth",
    IdentificationNumber = "IdentificationNumber",
    EuSocialInsuranceNumber = "EuSocialInsuranceNumber",
}

export const OtherIdentifierFields = [
    PrEmployeeEntity.ForeignPersonalIdentificationNumber,
    PrEmployeeEntity.EuHealthCareNumber,
    PrEmployeeEntity.EuSocialInsuranceNumber,
    PrEmployeeEntity.ForeignTaxIdentifierType,
    PrEmployeeEntity.ForeignTaxIdentifier
];

// Common init function, works with different prefixes + different identification property names as it's not consistent
export function initIdentificationTypeLocalPath(storage: Model, baseBc: BindingContext, identificationPropName: string): void {
    const localIdentBc = baseBc.navigate(IdentificationTypeLocalPath);
    if (storage.getValue(baseBc.navigate("DateOfBirth"))) {
        storage.setValue(localIdentBc, IdentificationType.DateOfBirth);
    } else if (storage.getValue(baseBc.navigate(identificationPropName))) {
        storage.setValue(localIdentBc, IdentificationType.IdentificationNumber);
    } else if (baseBc.isValidNavigation(PrEmployeeEntity.EuSocialInsuranceNumber) && storage.getValue(baseBc.navigate(PrEmployeeEntity.EuSocialInsuranceNumber))) {
        storage.setValue(localIdentBc, IdentificationType.EuSocialInsuranceNumber);
    }
}

interface IHandleIdentificationTypeBeforeSaveArgs {
    identificationPropName: string;
    useEuSocialInsuranceNumber?: boolean;
}

export function handleIdentificationTypeBeforeSave(data: IEntity, dataBindingContext: BindingContext, baseBc: BindingContext, args: IHandleIdentificationTypeBeforeSaveArgs): void {
    const typeBc = baseBc.navigate(IdentificationTypeLocalPath);
    const type = getBoundValue({ data, bindingContext: typeBc, dataBindingContext });
    const { identificationPropName, useEuSocialInsuranceNumber } = args;
    if (type) {
        [
            { type: IdentificationType.IdentificationNumber, prop: identificationPropName },
            (useEuSocialInsuranceNumber ? { type: IdentificationType.EuSocialInsuranceNumber, prop: PrEmployeeEntity.EuSocialInsuranceNumber }
                : { type: IdentificationType.DateOfBirth, prop: "DateOfBirth" }),
        ].forEach(t => {
            if (baseBc.isValidNavigation(t.prop) && t.type !== type) {
                const bc = baseBc.navigate(t.prop);
                setBoundValue({ data, dataBindingContext, bindingContext: bc, newValue: null, preventCloning: true });
            }
        });
    }
}

export function isOfIdentType(type: IdentificationType): TGetValueFn<boolean> {
    return ({ storage, bindingContext }) => {
        const itemBc = bindingContext.getParent();
        if (isFieldReadOnly(storage.getInfo(bindingContext), storage, bindingContext)) {
            // if field is readonly, we don't want to show the field at all,
            // as it's value is rendered inside readonly formatter of getIdentificationTypeSelectDef
            return false;
        }

        const val = storage.getValue(itemBc.navigate(IdentificationTypeLocalPath));
        return val === type;
    };
}

function getTranslatedIdentType(type: IdentificationType): string {
    switch (type) {
        case IdentificationType.DateOfBirth:
            return i18next.t("Employee:Form.DateOfBirth");
        case IdentificationType.IdentificationNumber:
            return i18next.t("Employee:Form.IdentificationNumber");
        case IdentificationType.EuSocialInsuranceNumber:
            return i18next.t("Employee:Form.EuSocialInsuranceNumber");
    }
}

function getValueForIdentType(type: IdentificationType, entityBc: BindingContext, args: IFormatOptions): string {
    let path: string;
    const { storage } = args;
    switch (type) {
        case IdentificationType.DateOfBirth:
            path = "DateOfBirth";
            break;
        case IdentificationType.IdentificationNumber:
            path = entityBc.isValidNavigation("PersonalIdentificationNumber") ? "PersonalIdentificationNumber" : "IdentificationNumber";
            break;
        case IdentificationType.EuSocialInsuranceNumber:
            path = "EuSocialInsuranceNumber";
            break;
    }
    if (!entityBc.isValidNavigation(path)) {
        return "";
    }
    const bc = entityBc.navigate(path);
    const val = storage.getValue(bc);
    const info = storage.getInfo(bc);
    return formatValue(val, info, args);
}

export function getIdentificationTypeSelectDef(hiddenTypes?: IdentificationType[]): Partial<IFieldDef> {
    const def: Partial<IFieldDef> = {
        type: FieldType.ComboBox,
        label: i18next.t("Employee:Form.IdentificationType"),
        width: BasicInputSizes.M,
        isRequired: true,
        defaultValue: IdentificationType.IdentificationNumber,
        formatter: (val: TValue, args: IFormatOptions): string => {
            const type = val as IdentificationType;
            const name = getTranslatedIdentType(type);
            if (args.readonly) {
                const entityBc = args.bindingContext.getParent();
                const identValue = getValueForIdentType(type, entityBc, args);
                return `${name}: ${identValue}`;
            }
            return name;
        },
        fieldSettings: {
            items: Object.values(IdentificationType).map(t => ({
                id: t,
                label: getTranslatedIdentType(t)
            }))
        }
    };
    if (hiddenTypes?.length) {
        def.fieldSettings.items = def.fieldSettings.items.filter(i => !hiddenTypes.includes(i.id as IdentificationType));
    }
    return def;
}

export function getForeignTaxIdentifierTypeDef(): Partial<IFieldDef> {
    return {
        type: FieldType.ComboBox,
        defaultValue: MpsvTaxIdentifierTypeCode.PersonalIdentificationNumber,
        fieldSettings: {
            displayName: MpsvTaxIdentifierTypeEntity.Name,
            transformFetchedItems: (items: ISelectItem[]): ISelectItem[] => {
                // other to the end of the list, the rest will stay in order
                return items.sort((a, b) =>
                    b.id === MpsvTaxIdentifierTypeCode.Other ? -1 : (a.id === MpsvTaxIdentifierTypeCode.Other ? 1 : 0));
            }
        },
    };
}

export interface IPrEmployeeEntityExtended extends IPrEmployeeEntity {
    [ShowCzechAddressPath]: boolean;
    [ShowContactAddressPath]: boolean;
    [PreviousEmploymentDurationDaysPath]: number;
    [PreviousEmploymentDurationYearsPath]: number;
    [ShowOtherIdentifiers]: boolean;
    [ShowDisabilityPath]: boolean;
    [ShowPensionPath]: boolean;
    [ShowForeignInsuranceCarrierPath]: boolean;
    [ShowStudyPath]: boolean;
    [ShowSocialInsuranceDiscountLocalPath]: boolean;
}

export const TAX_PAYER_DECLARATION_ACTION = "taxpayerDeclaration";
export const SEND_EMPLOYEE_INVITATION_ACTION = "sendEmployeeInvitation";
export const SEND_PAYSLIP_PASSWORD_ACTION = "sendPayslipPassword";

export enum SendToEmployeeActionType {
    SendEmployeeInvitation = "sendEmployeeInvitation",
    SendPayslipPassword = "sendPayslipPassword"
}

export interface IEmployeeFormCustomData extends ISmartTemporalPropertyDialogCustomData {
    submissionType?: PrSubmissionTypeCode;
    taxPayerDeclarationDialogOpen?: boolean;
    employments?: Partial<IPrEmploymentEntity>[];
    sendEmployeeDialogType?: SendToEmployeeActionType;
}

export const employeeNameFormatter = (val: TValue, args?: IFormatOptions<IPrEmployeeEntity, IPrEmployeeEntity>): string => {
    return universalPersonNameFormatter(args.item);
};

export enum EmployeePrintLayoutType {
    PayrollRecord = "PayrollRecord",
    EmployerConfirmationForAlimony = "EmployerConfirmationForAlimony",
    NetIncomeCertificate = "NetIncomeCertificate",
    AssessmentBasesConfirmation = "AssessmentBasesConfirmation",
    EmployerAssessmentBasesSS = "EmployerAssessmentBasesSS",
    EmploymentDurationCertificate = "EmploymentDurationCertificate",
    MinimumAssessmentBaseInsuranceCertificate = "MinimumAssessmentBaseInsuranceCertificate",
    TaxableIncomeCertificate = "TaxableIncomeCertificate",
    TaxableIncomeCertificateWithholdingTax = "TaxableIncomeCertificateWithholdingTax",
    EmployerTaxBenefitCertificate = "EmployerTaxBenefitCertificate",
    EmployerConfirmationForIncomeTax = "EmployerConfirmationForIncomeTax",
    IncomeAmountForSocialAssistanceBenefits = "IncomeAmountForSocialAssistanceBenefits",
}

export const EmployeePdfControllerMap = {
    [EmployeePrintLayoutType.PayrollRecord]: "PrPayrollSheetPdfExport",
    [EmployeePrintLayoutType.EmployerConfirmationForAlimony]: "PrEmployerConfirmationPdfExport",
    [EmployeePrintLayoutType.TaxableIncomeCertificate]: "PrEmploymentIncomeTaxPdfExport",
    [EmployeePrintLayoutType.EmploymentDurationCertificate]: "PrEmploymentSicknessConfirmationPdfExport",
    [EmployeePrintLayoutType.NetIncomeCertificate]: "PrEmployerIncomeConfirmationPdfExport",
    [EmployeePrintLayoutType.MinimumAssessmentBaseInsuranceCertificate]: "PrMinimumInsuranceContributionConfirmationPdfExport",
    [EmployeePrintLayoutType.EmployerConfirmationForIncomeTax]: "PrSecondParentTaxBenefitPdfExport",
    [EmployeePrintLayoutType.EmployerTaxBenefitCertificate]: "PrSecondParentTaxReliefPdfExport",
    [EmployeePrintLayoutType.EmployerAssessmentBasesSS]: "PrSocialSecurityAssessmentPdfExport",
    [EmployeePrintLayoutType.TaxableIncomeCertificateWithholdingTax]: "PrTaxableEmploymentIncomeCertificatePdfExport",
    [EmployeePrintLayoutType.AssessmentBasesConfirmation]: "PrEmployerTotalAssessmentBasesConfirmationPdfExport",
    [EmployeePrintLayoutType.IncomeAmountForSocialAssistanceBenefits]: "PrProofOfIncomePdfExport",
};

export const getEmployeeSelectDef = (additionalPropsToLoad: string[] = []): TFieldDefinition => ({
    type: FieldType.ComboBox,
    width: BasicInputSizes.XL,
    fieldSettings: {
        displayName: PrEmployeeEntity.FirstName,
        shouldDisplayAdditionalColumns: true,
        passItemContentToInput: true,
        transformFetchedItems: transformUserSelectItems,
        localDependentFields: [
            {
                from: { id: PrEmployeeEntity.FirstName },
                to: { id: PrEmployeeEntity.FirstName },
                navigateFrom: NavigationSource.Itself
            },
            {
                from: { id: PrEmployeeEntity.LastName },
                to: { id: PrEmployeeEntity.LastName },
                navigateFrom: NavigationSource.Itself
            },
            {
                from: { id: PrEmployeeEntity.NumberOurs },
                to: { id: PrEmployeeEntity.NumberOurs },
                navigateFrom: NavigationSource.Itself
            },
            ...additionalPropsToLoad.map(prop => ({
                from: { id: prop },
                to: { id: prop },
                navigateFrom: NavigationSource.Itself
            }))
        ]
    },
    formatter: employeeNameFormatter,
    columns: [
        {
            id: PrEmployeeEntity.FirstName,
            additionalProperties: [
                { id: PrEmployeeEntity.LastName },
                ...additionalPropsToLoad.map(prop => ({ id: prop }))
            ],
            formatter: employeeNameFormatter
        },
        { id: PrEmployeeEntity.NumberOurs }
    ],
    additionalProperties: [
        { id: PrEmployeeEntity.FirstName },
        { id: PrEmployeeEntity.LastName },
        { id: PrEmployeeEntity.NumberOurs },
        ...additionalPropsToLoad.map(prop => ({ id: prop }))
    ]
});

export function getEmployeeSummaryItemDef(employeeProp: string, isSingleEmployeeView?: boolean): ISummaryItem {
    return {
        id: employeeProp,
        formatter: (val: TValue, args: IFormatOptions) => {
            const { bindingContext } = args.storage.data;
            const employee = getBoundValue({
                data: args.entity,
                dataBindingContext: bindingContext,
                bindingContext: bindingContext.navigate(employeeProp)
            }) as IPrEmployeeEntity;
            return !isObjectEmpty(employee)
                ? (userNameWithAvatarFormatter(val ?? employee?.Id, {
                    ...args,
                    entity: employee
                }, { isBig: true }) as ICellValueObject)?.value
                : DASH_CHARACTER;
        },
        additionalProperties: [
            { id: PrEmployeeEntity.FirstName },
            { id: PrEmployeeEntity.LastName }
        ],
        isVisible: ifAny(not(isNewEntity), isSingleEmployeeView)
    };
}

export function getEmployeeFilterDef(employeeProp: string): Record<string, TFilterDef> {
    return withDisplayName(employeeProp, PrEmployeeEntity.NumberOurs, {
        filterName: [`/${employeeProp}/${PrEmployeeEntity.LastName}`, `/${employeeProp}/${PrEmployeeEntity.FirstName}`, `/${employeeProp}/${PrEmployeeEntity.NumberOurs}`],
        formatter: (val: TValue, args: IFormatOptions) => {
            const employee = getBoundValue({
                data: args.entity,
                dataBindingContext: args.storage?.data?.bindingContext,
                bindingContext: args.storage?.data?.bindingContext.navigate(employeeProp)
            }) as IPrEmployeeEntity;
            // note employee might not be defined before items are loaded, there is just filter value saved in Variant
            return employee?.NumberOurs ? universalPersonNameFormatter(employee as IPrEmployeeEntity)
                : val as string ?? args.storage.t("Common:General.Empty").toString();
        },
        // todo: ??? add multiple columns to Value help to show separately name and number
        // fieldSettings: {
        //     shouldDisplayAdditionalColumns: true,
        // },
        // columns: [
        //     {
        //         id: PrEmployeeEntity.LastName,
        //     },
        //     { id: PrEmployeeEntity.NumberOurs }
        // ],
        additionalProperties: [
            { id: PrEmployeeEntity.FirstName },
            { id: PrEmployeeEntity.LastName }
        ]
    });
}

export function getEmployeeTableColumnDefs(employeeProp: string): Record<string, IFieldInfoProperties> {
    return {
        ...withDisplayName(employeeProp, PrEmployeeEntity.LastName, {
                textAlign: TextAlign.Left,
                formatter: (val: TValue, args: IFormatOptions) => {
                    const employee = getBoundValue({
                        data: args.entity,
                        dataBindingContext: args.storage.data.bindingContext,
                        bindingContext: args.storage.data.bindingContext.navigate(employeeProp)
                    }) as IPrEmployeeEntity;
                    return userNameWithAvatarFormatter(val, {
                        ...args,
                        entity: employee
                    }, { isBold: false });
                },
                additionalProperties: [
                    { id: PrEmployeeEntity.FirstName }
                ]
            }
        ),
        [createPath(employeeProp, PrEmployeeEntity.NumberOurs)]: {
            formatter: (val: TValue, { entity, storage }: IFormatOptions): TCellValue => {
                const employee = getBoundValue({
                    data: entity,
                    dataBindingContext: storage.data.bindingContext,
                    bindingContext: storage.data.bindingContext.navigate(employeeProp)
                }) as IPrEmployeeEntity;
                const { Id } = employee ?? {};
                return getTableIntentLink(val as string, {
                    route: `${ROUTE_EMPLOYEES}/${Id}`,
                    context: storage.context,
                    storage: storage
                });
            }
        }
    };
}

export type TPersonWithIdentification = Pick<IPrEmployeeEntity, "Id" | "FirstName" | "LastName" | "DateOfBirth" | "PersonalIdentificationNumber">;

export function isPersonSame(p1: TPersonWithIdentification, p2: TPersonWithIdentification): boolean {
    const keys: (keyof TPersonWithIdentification)[] = [
        PrEmployeeEntity.FirstName,
        PrEmployeeEntity.LastName,
    ];
    const hasSameIdentification = (p1.DateOfBirth && p2.DateOfBirth && getUtcDayjs(p1.DateOfBirth).isSame(getUtcDayjs(p2.DateOfBirth)))
        || (p1.PersonalIdentificationNumber && p2.PersonalIdentificationNumber && p1.PersonalIdentificationNumber === p2.PersonalIdentificationNumber);

    return keys.every(k => (!p1[k] && !p2[k]) || p1[k] === p2[k])
        && hasSameIdentification;
}

// ----------- Definitions and callbacks form Employee + Employment combination on Form -----------------------------
export const employeeWithDependentEmploymentSelectDef: TFieldDefinition = {
    ...getEmployeeSelectDef(),
    isVisible: isNewEntity,
    filter: {
        select: `${PrEmployeeEntity.PrEmployeeStateCode} ne ${transformToODataString(PrEmployeeStateCode.None, ValueType.String)}`
    },
    clearIfInvisible: false,
};

export const getEmploymentSelectDef = (pathToEmployee: string): TFieldDefinition => ({
    type: FieldType.ComboBox,
    width: BasicInputSizes.XL,
    isVisible: (args: IGetValueArgs): boolean => {
        const info = args.storage.getInfo(args.bindingContext);
        return info?.fieldSettings?.items?.length > 1;
    },
    clearIfInvisible: false,
    isReadOnly: not(isNewEntity),
    fieldSettings: {
        displayName: createPath(PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name),
        items: [], // loaded dynamically based on Employee
        shouldDisplayAdditionalColumns: true,
        additionalProperties: [
            { id: PrEmploymentEntity.DateEnd },
            { id: PrEmploymentEntity.DateCreated },
        ],
        localDependentFields: [
            {
                from: { id: PrEmploymentEntity.NumberOurs },
                to: { id: PrEmploymentEntity.NumberOurs },
                navigateFrom: NavigationSource.Itself
            },
            {
                from: { id: PrEmploymentEntity.Type },
                to: { id: PrEmploymentEntity.Type },
                navigateFrom: NavigationSource.Itself
            }
        ]
    },
    filter: {
        select: (args: IGetValueArgs) => {
            const employee = args.storage.getValueByPath(pathToEmployee);
            return `${createPath(PrEmploymentEntity.Employee, PrEmploymentEntity.Id)} eq ${transformToODataString(employee?.Id, ValueType.Number)}`;
        }
    },
    columns: [
        { id: createPath(PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name) },
        { id: PrEmploymentEntity.NumberOurs }
    ],
    additionalProperties: [
        { id: PrEmploymentEntity.Type },
        { id: PrEmploymentEntity.NumberOurs },
    ]
});

export const transformWorkPermitTypeItems = sortItemsByPredefinedOrder([
    PrWorkPermitTypeCode.WorkPermit,
    PrWorkPermitTypeCode.EmployeeCard,
    PrWorkPermitTypeCode.CardForAnIntracompanyEmployeeTransfer,
    PrWorkPermitTypeCode.BlueCard,
]);

/**
 * Load employments and if there is no employment set on entity, set first employment as value
 * @param storage
 * @param bindingContext
 * @param handleChange
 */
export async function loadEmployments(storage: FormStorage, bindingContext: BindingContext, handleChange: ((e: ISmartFieldChange) => void)): Promise<void> {
    const info = storage.getInfo(bindingContext);

    const employmentItems = await fetchAndSetItemsByInfo(storage as unknown as FormStorage, info);
    const currentValue = storage.getValue(bindingContext, { useDirectValue: false });
    if (employmentItems.length && (!currentValue || !employmentItems?.find(ei => ei.id === currentValue))) {
        // get oldest employment https://solitea-cz.atlassian.net/browse/DEV-44308
        const def = employmentItems.filter(e => {
            const dateEnd = e.additionalData?.[PrEmploymentEntity.DateEnd];
            return !dateEnd || getUtcDayjs(dateEnd).isAfter(getUtcDayjs());
        })?.sort((a, b) => {
            const aCreated = a.additionalData?.[PrEmploymentEntity.DateCreated] ?? 0;
            const bCreated = b.additionalData?.[PrEmploymentEntity.DateCreated] ?? 0;
            return getUtcDayjs(aCreated).diff(bCreated);
        })[0];

        if (!def) {
            // only ended employments are available, do not set any value
            return null;
        }

        handleChange({
            value: def.id,
            selectedItems: [def],
            bindingContext,
            triggerAdditionalTasks: true,
            additionalData: def.additionalData
        });
    }
}

export function getEmployeeSalaryCurrencyPath(pathToEmployee: string): string {
    return createPath(pathToEmployee, PrEmployeeEntity.SalaryCurrencyCode);
}

/**
 * Loads data about employee's employments and stores them in customData to be used on sync callbacks
 * @param id
 * @param storage
 */
export async function getEmployeeEmployments(id: TEntityKey, storage: FormStorage<IPrEmployeeEntityExtended, IEmployeeFormCustomData>): Promise<void> {
    let employments: IPrEmploymentEntity[] = [];
    try {
        const query = storage.oData.getEntitySetWrapper(EntitySetName.PrEmployments).query()
            .filter(`${PrEmploymentEntity.Employee}/Id eq ${transformToODataString(id, ValueType.Number)}`)
            .select(PrEmploymentEntity.Id, PrEmploymentEntity.TypeCode, PrEmploymentEntity.DateStart, PrEmploymentEntity.DateEnd);

        const res = await query.fetchData<IPrEmploymentEntity[]>();
        employments = res.value;
    } catch (e) {
        // no action, employments will be empty
    }

    storage.setCustomData({ employments });
}

export const getSalaryCurrencyFactory = (pathToEmployee = ""): TGetValueFn<ICurrencyEntity> => {
    return ({ storage }) => {
        const currency = storage.getValueByPath(createPath(pathToEmployee, PrEmployeeEntity.SalaryCurrency));
        return currency ?? getCompanyCurrencyEntity(storage.context);
    };
};

export function getUserId(context: IAppContext): number {
    const authContext = context.getAuthContext();
    return authContext.userId;
}

export async function getEmployeeForCurrentUser(storage: Model): Promise<IPrEmployeeEntity> {
    const { context, oData } = storage;
    try {
        const query = oData.getEntitySetWrapper(EntitySetName.PrEmployees).query()
            .filter(`User/Id eq ${getUserId(context)}`)
            .select(PrEmployeeEntity.Id, PrEmployeeEntity.FirstName, PrEmployeeEntity.LastName);

        const res = await query.fetchData<IPrEmployeeEntity[]>();
        return res.value?.[0];
    } catch (e) {
        return null;
    }
}

export const getClosedEmployments = async (oData: OData, year: number, month: number): Promise<IPrEmploymentEntity[]> => {
    const wrapper = oData.getEntitySetWrapper(EntitySetName.PrEmployments);
    const toDate = getUtcDayjs().set("year", year).set("month", month - 1).startOf("month");
    try {
        const query = wrapper.query()
            .filter(`${createPath(PrEmploymentEntity.DateEnd)} lt ${transformToODataString(toDate, ValueType.Date)}`)
            .select(PrEmploymentEntity.NumberOurs, PrEmploymentEntity.TypeCode, PrEmploymentEntity.Id)
            .expand(PrEmploymentEntity.Type, q => q.select(PrEmploymentTypeEntity.Name))
            .expand(PrEmploymentEntity.Employee, q2 => q2.select(PrEmployeeEntity.Id, PrEmployeeEntity.NumberOurs, PrEmployeeEntity.FirstName, PrEmployeeEntity.LastName));

        const res = await query.fetchData<IPrEmploymentEntity[]>();
        return res.value;
    } catch (e) {
        logger.error("Error fetching closed employments", e);
        return [];
    }
};
